import { CarouselRoot, CarouselContainer, CarouselItem } from '../../Carousel.styles'
import Image, { StaticImageData } from 'next/image'
import { Text } from 'components/Text'
import Bruce from '../../../../../public/assets/our-story/company-founded/carousel/bruce-dad.webp'
import Rich from '../../../../../public/assets/our-story/company-founded/carousel/rich-with-boxes.webp'
import ProductShot from '../../../../../public/assets/our-story/company-founded/carousel/wfr-product-shot.webp'
import Freezer from '../../../../../public/assets/our-story/company-founded/carousel/freezers.webp'
import Usda from '../../../../../public/assets/our-story/company-founded/carousel/usda-chicago.webp'
import BrandedBoxes from '../../../../../public/assets/our-story/company-founded/carousel/branded-boxes.webp'
import useTranslation from 'next-translate/useTranslation'
import { Icon } from 'components/Icon'
import { useEffect, useRef, useState } from 'react'
import { Selector, SelectorsContainer } from '../../OurStory.styles'

export type FoundedImagesTypes = {
  src: StaticImageData
  alt: string
  caption: string
}

export const CompanyFoundedCarousel = ({ isDesktop }: any) => {
  const { t } = useTranslation()

  const [currentSlide, setCurrentSlide] = useState<number>(0)

  const foundedSliderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isDesktop) return
    const slider = foundedSliderRef.current
    if (!slider) return

    let touchStartX = 0
    let touchEndX = 0

    const handleTouchStart = (event: TouchEvent) => {
      touchStartX = event.changedTouches[0].screenX
    }

    const handleTouchMove = (event: TouchEvent) => {
      touchEndX = event.changedTouches[0].screenX
    }

    const handleTouchEnd = () => {
      if (touchEndX < touchStartX) {
        nextSlide()
      } else if (touchEndX > touchStartX) {
        previousSlide()
      }
    }

    slider.addEventListener('touchstart', handleTouchStart, false)
    slider.addEventListener('touchmove', handleTouchMove, false)
    slider.addEventListener('touchend', handleTouchEnd, false)

    // Cleanup event listeners on component unmount
    return () => {
      slider.removeEventListener('touchstart', handleTouchStart)
      slider.removeEventListener('touchmove', handleTouchMove)
      slider.removeEventListener('touchend', handleTouchEnd)
    }
  }, [currentSlide, isDesktop])

  const previousSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1)
    }
  }

  const nextSlide = () => {
    if (!isDesktop && currentSlide < foundedImages.length - 1) {
      setCurrentSlide(currentSlide + 1)
    }
  }

  const jumpSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const foundedImages: FoundedImagesTypes[] = [
    {
      src: Rich,
      alt: t('our-story:company-founded.carousel.rich.alt'),
      caption: t('our-story:company-founded.carousel.rich.caption'),
    },
    {
      src: Bruce,
      alt: t('our-story:company-founded.carousel.bruce.alt'),
      caption: t('our-story:company-founded.carousel.bruce.caption'),
    },
    {
      src: Freezer,
      alt: t('our-story:company-founded.carousel.freezer.alt'),
      caption: t('our-story:company-founded.carousel.freezer.caption'),
    },
    {
      src: Usda,
      alt: t('our-story:company-founded.carousel.usda.alt'),
      caption: t('our-story:company-founded.carousel.usda.caption'),
    },
    {
      src: BrandedBoxes,
      alt: t('our-story:company-founded.carousel.branded-boxes.alt'),
      caption: t('our-story:company-founded.carousel.branded-boxes.caption'),
    },
    {
      src: ProductShot,
      alt: t('our-story:company-founded.carousel.product-shot.alt'),
      caption: t('our-story:company-founded.carousel.product-shot.caption'),
    },
  ]

  return (
    <>
      <CarouselRoot>
        <button
          style={{ transform: 'translate(-3rem, -4rem)', position: 'absolute', left: '0' }}
          onClick={() => setCurrentSlide(currentSlide - 1)}
          className="arrow"
          disabled={currentSlide == 0}
        >
          <Icon color="accent-black" size="6" name="angle-left" />
        </button>
        <CarouselContainer scroll="none" ref={foundedSliderRef}>
          {foundedImages.map((image, index) => (
            <CarouselItem width="third" mobileAlign="left" slide={currentSlide} key={index}>
              <Image src={image.src} alt={image.alt} />
              <Text
                className="caption"
                style={{ marginTop: '1rem', width: '100%' }}
                as="p"
                size="body"
              >
                {image.caption}
              </Text>
            </CarouselItem>
          ))}
        </CarouselContainer>
        <button
          style={{ transform: 'translate(3rem, -4rem)', position: 'absolute', right: '0' }}
          onClick={() => setCurrentSlide(currentSlide + 1)}
          className="arrow"
          disabled={
            isDesktop
              ? currentSlide == foundedImages.length - 3
              : currentSlide == foundedImages.length - 1
          }
        >
          <Icon color="accent-black" size="6" name="angle-right" />
        </button>
      </CarouselRoot>
      {!isDesktop && (
        <SelectorsContainer padding="med" gap="2">
          {foundedImages.map((_, index) => (
            <Selector
              className="selector"
              key={index}
              active={index === currentSlide}
              onClick={() => jumpSlide(index)}
              aria-label={t('our-story:company-founded.carousel.aria', { index: index + 1 })}
            />
          ))}
        </SelectorsContainer>
      )}
    </>
  )
}
